import { zodResolver } from '@hookform/resolvers/zod';
import { Cancel, InfoOutlined } from '@mui/icons-material';
import {
  Drawer,
  Grid,
  Typography,
  Divider,
  FormControl,
  FormLabel,
  TextField,
  Button,
  Autocomplete,
  Checkbox,
  FormControlLabel,
  Alert,
  Tooltip
} from '@mui/material';
import { CreateOrPutCustomerInput, Customer } from '@thro-types/billing';

import { useEffect, useRef } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useInjectReducer } from 'redux-injectors';
import { validate as uuidValidate } from 'uuid';

import { useCustomerMutation, useInvoiceCustomerMutation } from 'api/hooks/billing/customer';
import { useInvoiceMutation } from 'api/hooks/billing/invoices';
import { SaveButton } from 'app/components';
import { AlertActions } from 'app/containers/Dashboard/Alert/slice';
import { translations } from 'locales/i18n';
import { countries, countryToFlag, CountryType } from 'utils/Countries';
import useScrollToRef from 'utils/hooks/useScrollToRef';

import { selectCustomerDrawer } from './customerDrawerSelectors';
import {
  CustomerDrawerActions,
  CustomerDrawerReducer,
  CustomerDrawerSliceKey
} from './customerDrawerSlice';
import { customerValidationSchema, useCustomerInitialValues } from './validation';

export const CustomerDrawer = () => {
  useInjectReducer({ key: CustomerDrawerSliceKey, reducer: CustomerDrawerReducer });
  const customerDrawer = useSelector(selectCustomerDrawer);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const alertRef = useRef<HTMLDivElement>(null);
  const { scrollToRef } = useScrollToRef({
    targetRef: alertRef,
    callback: () => {},
    smoothCallback: () => {}
  });

  const { create, update } = useCustomerMutation();
  const { update: updateInvoiceCustomer } = useInvoiceCustomerMutation();
  const { update: updateInvoice } = useInvoiceMutation();

  const { watch, handleSubmit, formState, control, register, clearErrors, reset, setValue } =
    useForm<
      CreateOrPutCustomerInput & {
        id?: string;
        saveCustomer: boolean;
        isCensored?: boolean;
      }
    >({
      values: useCustomerInitialValues(customerDrawer.customer),
      resolver: zodResolver(customerValidationSchema())
    });

  const isPending =
    create.isPending ||
    update.isPending ||
    updateInvoiceCustomer.isPending ||
    updateInvoice.isPending;

  const handleClose = () => {
    reset();
    dispatch(CustomerDrawerActions.close());
  };

  const handleSaveClick = async values => {
    try {
      if (isPending) {
        return;
      }

      if (!values.phone.length) {
        delete values.phone;
      }

      if (customerDrawer.customer?.invoice && !values.saveCustomer) {
        delete values.id;
        delete values.saveCustomer;

        await updateInvoiceCustomer.mutateAsync({
          id: customerDrawer.customer.invoice.id,
          data: values
        });
        dispatch(
          AlertActions.success(t(translations.pages.billingCustomer.alerts.updateSuccess()))
        );
        handleClose();

        return;
      }

      delete values.saveCustomer;

      let newCustomer: Customer;

      if (!customerDrawer.customer) {
        await create.mutateAsync(values);
        dispatch(
          AlertActions.success(t(translations.pages.billingCustomer.alerts.createSuccess()))
        );
        handleClose();
      } else {
        newCustomer = await update.mutateAsync({
          key: customerDrawer.customer.customerId,
          data: values
        });
        dispatch(
          AlertActions.success(t(translations.pages.billingCustomer.alerts.updateSuccess()))
        );

        if (newCustomer && customerDrawer.customer.invoice) {
          await updateInvoice.mutateAsync({
            id: customerDrawer.customer.invoice.id,
            data: { customerVersionId: newCustomer.id }
          });
        }
        handleClose();
      }
    } catch (error) {
      if (!customerDrawer.customer) {
        dispatch(AlertActions.error(t(translations.pages.billingCustomer.alerts.createFailed())));
      } else {
        dispatch(AlertActions.error(t(translations.pages.billingCustomer.alerts.updateFailed())));
      }
    }
  };

  const shouldSaveCustomer = watch('saveCustomer');
  useEffect(() => {
    if (shouldSaveCustomer) {
      scrollToRef();
    }
  }, [shouldSaveCustomer, scrollToRef]);

  const companyAndUstID = !!watch('companyName') && !!watch('ustId');

  return (
    <Drawer
      anchor="right"
      open={customerDrawer.open}
      onClose={() => {
        dispatch(CustomerDrawerActions.close());
        clearErrors();
      }}
      PaperProps={{
        sx: { width: { xs: '70%', md: '35%', xl: '25%' } }
      }}
    >
      <form
        onSubmit={handleSubmit(values => handleSaveClick(values))}
        style={{ display: 'flex', flex: 1 }}
      >
        <Grid
          container
          padding={'1em'}
          flex={1}
          alignContent={'flex-start'}
          flexDirection={'column'}
          gap={1}
        >
          <Typography variant="h5" fontWeight={'bold'}>
            {watch('id')
              ? t(translations.pages.billingCustomer.drawer.editTitle())
              : t(translations.pages.billingCustomer.drawer.createTitle())}
          </Typography>
          <Divider />
          <Grid
            container
            maxHeight={'85vh'} // set a max height for the scrolling area
            sx={{ overflowY: 'auto' }} // enable scrolling
            gap={1}
          >
            <Grid container item gap={1}>
              <FormControl fullWidth disabled>
                <FormLabel
                  sx={{
                    display: 'inline-flex',
                    alignItems: 'center',
                    justifyContent: 'space-between'
                  }}
                >
                  {t(translations.pages.billing.invoiceCreate.customerId())}
                  <Tooltip title={t(translations.pages.billing.invoiceCreate.customerIdTooltip())}>
                    <InfoOutlined style={{ opacity: 0.8 }} />
                  </Tooltip>
                </FormLabel>

                <TextField
                  // {...register('customerId')}
                  id="customerId"
                  name="customerId"
                  value={uuidValidate(watch('customerId')) ? '' : watch('customerId')}
                  size="small"
                  disabled
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl required={!(watch('ustId') && watch('companyName'))} fullWidth>
                <FormLabel>{t(translations.common.firstName())}</FormLabel>
                <TextField
                  {...register('firstName')}
                  id="firstName"
                  name="firstName"
                  error={companyAndUstID ? false : Boolean(formState.errors.firstName)}
                  helperText={companyAndUstID ? '' : formState.errors.firstName?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl required={!(watch('ustId') && watch('companyName'))} fullWidth>
                <FormLabel>{t(translations.common.lastName())}</FormLabel>
                <TextField
                  {...register('lastName')}
                  id="lastName"
                  name="lastName"
                  error={companyAndUstID ? false : Boolean(formState.errors.lastName)}
                  helperText={companyAndUstID ? '' : formState.errors.lastName?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl required fullWidth>
                <FormLabel>{t(translations.common.email())}</FormLabel>
                <TextField
                  {...register('email')}
                  id="email"
                  name="email"
                  error={Boolean(formState.errors.email)}
                  helperText={formState.errors.email?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl fullWidth>
                <FormLabel>{t(translations.common.phone())}</FormLabel>
                <TextField
                  {...register('phone')}
                  id="phone"
                  name="phone"
                  error={Boolean(formState.errors.phone)}
                  helperText={formState.errors.phone?.message}
                  size="small"
                  onChange={e => setValue('phone', e.target.value)}
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl fullWidth>
                <FormLabel>{t(translations.common.companyName())}</FormLabel>
                <TextField
                  {...register('companyName')}
                  id="companyName"
                  name="companyName"
                  error={Boolean(formState.errors.companyName)}
                  helperText={formState.errors.companyName?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl required={!!watch('companyName')} fullWidth>
                <FormLabel>{t(translations.common.taxId_short())}</FormLabel>
                <TextField
                  {...register('ustId')}
                  id="ustId"
                  name="ustId"
                  error={Boolean(formState.errors.ustId)}
                  helperText={formState.errors.ustId?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1} flex={1}>
              <FormControl required fullWidth>
                <FormLabel>{t(translations.common.zip())}</FormLabel>
                <TextField
                  {...register('zip')}
                  id="zip"
                  name="zip"
                  error={Boolean(formState.errors.zip)}
                  helperText={formState.errors.zip?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1} flex={3}>
              <FormControl required fullWidth>
                <FormLabel>{t(translations.common.city())}</FormLabel>
                <TextField
                  {...register('city')}
                  id="city"
                  name="city"
                  error={Boolean(formState.errors.city)}
                  helperText={formState.errors.city?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl required fullWidth>
                <FormLabel>{t(translations.common.street())}</FormLabel>
                <TextField
                  {...register('address')}
                  id="address"
                  name="address"
                  error={Boolean(formState.errors.address)}
                  helperText={formState.errors.address?.message}
                  size="small"
                />
              </FormControl>
            </Grid>
            <Grid container item gap={1}>
              <FormControl required fullWidth>
                <FormLabel>{t(translations.common.country())}</FormLabel>
                <Controller
                  name="country"
                  control={control}
                  render={({ field }) => (
                    <Autocomplete
                      options={countries as CountryType[]}
                      autoHighlight
                      fullWidth
                      value={
                        countries.find(
                          c => c.label === watch('country') || c.code === watch('country')
                        ) || null
                      }
                      getOptionLabel={option => option.label}
                      renderOption={({ key, ...props }, option) => (
                        <li {...props} key={key}>
                          <span style={{ marginRight: '0.5em' }}>{countryToFlag(option.code)}</span>
                          {option.label} ({option.code})
                        </li>
                      )}
                      onChange={(e, value) => {
                        field.onChange(value?.code || '');
                      }}
                      renderInput={params => (
                        <TextField
                          {...params}
                          fullWidth
                          error={Boolean(formState.errors.country)}
                          helperText={formState.errors.country?.message}
                          size="small"
                        />
                      )}
                    />
                  )}
                />
              </FormControl>
            </Grid>
            {customerDrawer.customer?.invoice &&
              !customerDrawer.customer.isCensored &&
              customerDrawer.customer.ustId && (
                <Controller
                  control={control}
                  name="saveCustomer"
                  render={({ field }) => (
                    <FormControlLabel
                      control={<Checkbox />}
                      label={t(translations.pages.billing.invoiceCreate.customerWrite())}
                      checked={watch('saveCustomer')}
                      value={watch('saveCustomer')}
                      onChange={(e, checked) => field.onChange(checked)}
                    />
                  )}
                />
              )}
            {watch('saveCustomer') && (
              <Alert severity="warning" ref={alertRef}>
                {t(translations.pages.billing.invoiceCreate.saveCustomerInfo())}
              </Alert>
            )}
          </Grid>
          <Grid
            container
            item
            justifyContent={'flex-end'}
            gap={2}
            alignItems={'flex-end'}
            marginTop={'auto'}
          >
            <Grid item>
              <Button
                variant="contained"
                color="error"
                startIcon={<Cancel />}
                onClick={() => dispatch(CustomerDrawerActions.close())}
                size="small"
                disabled={isPending}
              >
                {t(translations.common.cancel())}
              </Button>
            </Grid>
            <SaveButton isSubmitting={isPending} size="small" />
          </Grid>
        </Grid>
      </form>
    </Drawer>
  );
};
